import { useCallback, useEffect, useRef } from "react";
import type { useZero } from "@rocicorp/zero/react";
import { resolveAttachmentUrl } from "@/lib/attachments";
import { queries } from "@/lib/queries";
import type {
  PayloadAttachment,
  PayloadAttendee,
  PayloadParticipant,
} from "@ano/shared/payloads/schema";
import type { MessageKind } from "@ano/shared";
import type { RenderUser } from "@/lib/render-user";
import { isElectronRuntime } from "@/lib/logout";

interface MessageListItem {
  id: string;
  content: string;
  user_id: string;
  thread_id?: string | null;
  created_at: number;
  kind?: string | null;
  payload?: Record<string, unknown> | null;
  preview_text?: string | null;
  mentions?: string[] | null;
}

interface UseSendToShellArgs {
  channelId: string | null;
  channelName: string | null | undefined;
  activeThreadId?: string | null;
  messageById: Map<string, MessageListItem>;
  renderUserMap: Map<string, RenderUser>;
  zero: ReturnType<typeof useZero>;
}

/**
 * "Send to shell" — relays a chat message into the integrated terminal
 * via a `message:send-to-shell` window event. The shell terminal's
 * payload-injection path picks it up on the next user Enter.
 *
 * Hydrates the message via Zero's `byIdWithRelated` compound query so
 * the terminal receives a fully-typed envelope: kind + payload +
 * preview_text + mentions + attachments + attendees + participants
 * (call or email). Falls back to the un-hydrated list-item envelope on
 * query failure so the text path still delivers content.
 *
 * Stable identity — reads `messageById` / `renderUserMap` /
 * `channelName` through refs so MessageRow's `memo` doesn't bust on
 * every Zero push. Only `channelId` changes invalidate the callback.
 */
export function useSendToShell({
  channelId,
  channelName,
  activeThreadId,
  messageById,
  renderUserMap,
  zero,
}: UseSendToShellArgs) {
  const messageByIdRef = useRef(messageById);
  const renderUserMapRef = useRef(renderUserMap);
  const channelNameRef = useRef(channelName);
  const activeThreadIdRef = useRef(activeThreadId ?? null);
  useEffect(() => {
    messageByIdRef.current = messageById;
    renderUserMapRef.current = renderUserMap;
    channelNameRef.current = channelName;
    activeThreadIdRef.current = activeThreadId ?? null;
  });

  const sendToShell = useCallback(
    (
      messageId: string,
      selection?: string,
      msgOverride?: {
        id: string;
        content: string;
        user_id: string;
        thread_id?: string | null;
        created_at: number;
      },
    ) => {
      const msg = msgOverride ?? messageByIdRef.current.get(messageId);
      if (!msg || !channelId) return;
      const author = renderUserMapRef.current.get(msg.user_id);
      // Producers no longer emit <!--card:--> wrappers (Phase 2+3); content
      // on card-kind messages is the empty string or caption. Skip "empty
      // content without selection" so pure card relays carry their payload
      // rather than a stray whitespace message.
      const cleanContent = msg.content.trim();
      if (!cleanContent && !selection) return;

      // Phase 5: hydrate via a single compound `.related()` query — the
      // idiomatic Zero pattern (see .claude/rules/queries.md). One pipeline
      // + one CVR entry vs. N parallel `zero.run()` fan-outs. Typed fields
      // on the parent row (kind, payload, preview_text, mentions) come back
      // alongside the related rows. Async IIFE keeps the caller contract
      // void-returning. Graceful fallback on query failure.
      void (async () => {
        const fromListItem = messageByIdRef.current.get(messageId);
        let kind: MessageKind =
          (fromListItem?.kind as MessageKind | undefined) ?? "text";
        let payload =
          (fromListItem?.payload as Record<string, unknown> | null) ?? null;
        let previewText = fromListItem?.preview_text ?? null;
        // Phase 7c-D1b: `fromListItem.mentions` is now junction-derived
        // (the channel-message-list hook maps `message_mentions` →
        // `mentions: string[]` per row before stashing in the byId
        // Map). Both fast-path and hydrated path read junction data.
        let mentionsArr = Array.isArray(fromListItem?.mentions)
          ? (fromListItem?.mentions as string[])
          : [];
        let attachments: PayloadAttachment[] = [];
        let attendees: PayloadAttendee[] = [];
        let participants: PayloadParticipant[] = [];

        try {
          const row = await zero.run(queries.messages.byIdWithRelated(msg.id));
          if (row) {
            kind = (row.kind as MessageKind | undefined) ?? kind;
            payload =
              (row.payload as Record<string, unknown> | null) ?? payload;
            previewText = row.preview_text ?? previewText;
            // Phase 7c-D1a/b: source mentions from the junction
            // (hydrated via `byIdWithRelated`'s
            // `.related("message_mentions")` chain). Both this hydrated
            // path and the `fromListItem.mentions` fast-path above are
            // now junction-derived — `messages.mentions` JSONB is no
            // longer read by desktop code. `.sort()` matches the
            // fast-path's order so the same logical mention set
            // produces an identical envelope regardless of which path
            // hydrates it.
            if (Array.isArray(row.message_mentions)) {
              mentionsArr = row.message_mentions.map((mm) => mm.user_id).sort();
            }
            attachments = (row.attachments ?? []).map((a) => ({
              url: resolveAttachmentUrl(a),
              file_category: a.file_category ?? "",
              filename: a.filename ?? undefined,
              width: a.width ?? undefined,
              height: a.height ?? undefined,
            }));
            attendees = (row.calendar_attendees ?? []).map((r) => ({
              email: r.email ?? undefined,
              user_id: r.user_id ?? undefined,
              rsvp_status: r.rsvp_status ?? undefined,
            }));
            const emailParts = (row.email_participants ?? []).map((r) => ({
              email: r.email,
              role: r.role ?? undefined,
            }));
            const callParts = (row.call_participants ?? []).map((r) => ({
              user_id: r.user_id,
              joined_at: r.joined_at ?? undefined,
              left_at: r.left_at ?? undefined,
            }));
            participants = emailParts.length > 0 ? emailParts : callParts;
          }
        } catch {
          // Fall back to un-hydrated envelope on query failure — the text
          // path still delivers content via CustomEvent below.
        }

        window.dispatchEvent(
          new CustomEvent("message:send-to-shell", {
            detail: {
              messageId: msg.id,
              channelId,
              channelName: channelNameRef.current ?? "",
              threadId: activeThreadIdRef.current ?? msg.thread_id ?? null,
              authorId: msg.user_id,
              authorHandle: author?.display_name ?? "",
              authorName: author?.display_name ?? "",
              content: cleanContent,
              selection: selection ?? null,
              timestamp: msg.created_at,
              kind,
              previewText,
              payload,
              attendees,
              participants,
              attachments,
              mentions: mentionsArr,
            },
          }),
        );
      })();
    },
    [channelId, zero],
  );

  // The Shell is Electron-only — there's no terminal panel to receive
  // the event in a browser. Return undefined so consumer props (e.g.
  // MessageRow's optional `onSendToShell`) drop the action button.
  return isElectronRuntime() ? sendToShell : undefined;
}
